import { TRIAL_DAYS } from "@/brand/plan-numbers"

import { ATO_RECORDS_SOURCE, OVERSEAS_SENTENCE, STORAGE_SENTENCE } from "./trust-facts"

/** Copy source: docs/design/landing/copy.md section 2.7 and the web-landing-faq design. */

export interface FaqEntry {
  question: string
  /** Plain-text answer (also used for FAQPage JSON-LD); `sourced` adds a source line on the page. */
  answer: string
  sources?: [{ label: string; href: string }, ...{ label: string; href: string }[]]
}

export interface FaqGroup {
  id: string
  title: string
  items: FaqEntry[]
}

export const ATO_LOGBOOK = {
  label: "ATO, logbook method",
  href: "https://www.ato.gov.au/businesses-and-organisations/income-deductions-and-concessions/income-and-deductions-for-business/deductions/deductions-for-motor-vehicle-expenses/logbook-method",
}

export const FAQ_CHECKED = "28 Oct 2026"
export const FAQ_REVIEWED = "28 October 2026"

export const FAQ_GROUPS: FaqGroup[] = [
  {
    id: "records",
    title: "Records and the ATO",
    items: [
      {
        question: "How long should I keep my records?",
        answer:
          "The ATO says to keep most business records for 5 years. Export them any time. After a paid plan lapses we keep your data for 3 months at most and remind you several times to export it before it is removed from your account.",
        sources: [ATO_RECORDS_SOURCE],
      },
      {
        question: "Is this approved by the ATO?",
        answer:
          "No. We do not claim ATO approval. We build the logbook around the details the ATO lists on its logbook method page, and we link to the ATO page for each rule.",
        sources: [ATO_LOGBOOK],
      },
    ],
  },
  {
    id: "privacy",
    title: "Privacy and data",
    items: [
      {
        question: "Where is my data stored?",
        answer: `${STORAGE_SENTENCE} ${OVERSEAS_SENTENCE} The trust page lists them.`,
      },
      {
        question: "Can I export or delete my data?",
        answer:
          "You can export trips, receipts and invoices as CSV or PDF. You can delete your account in Settings or on the web.",
      },
      {
        question: "How do I export my data?",
        answer:
          'Sign in on the website, open your account and choose "Export all my data". You get a zip file of your records. Export before you delete anything.',
      },
      {
        question: "How do I delete my account?",
        answer:
          "Delete it from your account on the website. It is removed after a 30-day window in which you can restore it; the steps are on the Delete your account page. If you cannot sign in, email us.",
      },
    ],
  },
  {
    id: "app",
    title: "The app",
    items: [
      {
        question: "Does it work offline?",
        answer: "Yes. Trips and receipts are saved on your phone and sync later.",
      },
      {
        question: "Which phones are supported?",
        answer: "iPhone and Android. The website is for reviewing, invoicing and reports.",
      },
      {
        question: "My sign-in code did not arrive or has expired. What now?",
        answer:
          "We email a 6-digit code that is valid for 10 minutes. Check your spam folder, make sure you typed the same email address, then ask for a new code on the sign-in page. If nothing arrives after a few tries, email us.",
      },
      {
        question: "How do I send you the app logs?",
        answer:
          "In the iPhone app, open Settings, then Diagnostics, then Share logs, and attach the file to your email. Coordinates, email addresses and tokens are removed from it first.",
      },
    ],
  },
  {
    id: "billing",
    title: "Billing",
    items: [
      {
        question: "Do I need a card for the trial?",
        answer: `No. The ${TRIAL_DAYS}-day free trial needs no card.`,
      },
      {
        question: "Where do I manage billing and payment?",
        answer:
          "On the website only, in your account. The iPhone and Android apps do not take payments, so there is nothing to manage in your Apple or Google settings.",
      },
    ],
  },
]
